import { useEffect } from 'react';
import { useStoreSettings } from './useStoreSettings';

/**
 * Advanced hook for dynamic page titles that automatically uses store name from database
 * @param {string} pageType - Type of page ('home', 'checkout', 'shop', 'auth', etc.)
 * @param {string} customTitle - Custom title for specific pages (e.g., product name)
 * @param {string} variant - Product variant for product pages
 */
export const useDynamicPageTitle = (pageType = 'home', customTitle = null, variant = null) => {
    const { storeSettings, loading } = useStoreSettings();

    useEffect(() => {
        // Don't set title if store settings are still loading
        if (loading || !storeSettings) return;

        const storeName = storeSettings.name || 'Store';
        let title = '';

        switch (pageType) {
            case 'home':
                title = storeName;
                break;
            case 'checkout':
                title = `Checkout - ${storeName}`;
                break;
            case 'shop':
                title = `Shop - ${storeName}`;
                break;
            case 'cart':
                title = `Shopping Cart - ${storeName}`;
                break;
            case 'auth':
                title = `Sign In - ${storeName}`;
                break;
            case 'orders':
                title = `My Orders - ${storeName}`;
                break;
            case 'profile':
                title = `My Profile - ${storeName}`;
                break;
            case 'order-confirmation':
                title = `Order Confirmation - ${storeName}`;
                break;
            case 'product':
                if (customTitle) {
                    title = customTitle;
                    if (variant) {
                        title += ` - ${variant}`;
                    }
                    title += ` - ${storeName}`;
                } else {
                    title = storeName;
                }
                break;
            case 'custom':
                title = customTitle ? `${customTitle} - ${storeName}` : storeName;
                break;
            default:
                title = storeName;
        }

        document.title = title;

        // Cleanup function to restore store name on unmount
        return () => {
            document.title = storeName;
        };
    }, [pageType, customTitle, variant, storeSettings, loading]);
};

/**
 * Hook specifically for product pages with automatic product name handling
 * @param {object} product - Product object
 * @param {object} selectedVariant - Selected product variant
 * @param {string} language - Current language
 */
export const useProductPageTitle = (product, selectedVariant = null, language = 'en') => {
    const { storeSettings, loading } = useStoreSettings();

    useEffect(() => {
        if (loading || !storeSettings || !product) return;

        const storeName = storeSettings.name || 'Store';

        // Use the same logic as generatePageTitle but with dynamic store name
        let title = '';

        // Use multilingual product name or fallback
        if (product.name) {
            if (typeof product.name === 'object' && product.name[language]) {
                title = product.name[language];
            } else if (typeof product.name === 'string') {
                title = product.name;
            }
        }

        // Add variant information if available
        if (selectedVariant && selectedVariant.displayVariant) {
            title += ` - ${selectedVariant.displayVariant}`;
        }

        // Add store name
        title += ` - ${storeName}`;

        document.title = title;

        // Cleanup function
        return () => {
            document.title = storeName;
        };
    }, [product, selectedVariant, language, storeSettings, loading]);
};